import React, { useState, useEffect } from "react";
import TimeLogsTable from "./TimeLogsTable";
import AddTimeLogModal from "./AddTimeLogModal";
import EditTimeLogModal from "./EditTimeLogModal";
import BulkUploadTimeLogs from "./BulkUploadTimeLogs"; // Import the new component
import { Plus, Upload } from "lucide-react"; // Import Upload icon
import { toast } from "react-toastify";
import { getMergedData, updateStoredData } from "../lib/dataUtils";

const TimeLogs = () => {
  const [timeLogs, setTimeLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentTimeLog, setCurrentTimeLog] = useState(null);

  // New state for opportunities
  const [opportunities, setOpportunities] = useState([]);
  const [isOpportunitiesLoading, setIsOpportunitiesLoading] = useState(true);
  const [students, setStudents] = useState([]);

  // New state for bulk upload modal
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);

  // Load time logs from localStorage on component mount
  useEffect(() => {
    setIsLoading(true);
    const fetchedTimeLogs = getMergedData("timeLogs");
    const timeLogs = fetchedTimeLogs.sort((a, b) => {
      const statusOrder = { pending: 0, approved: 1, rejected: 2 };
      return statusOrder[a.status] - statusOrder[b.status];
    });
    setIsLoading(false);

    const fetchedOpportunities = getMergedData("opportunities");
    const fetchedStudents = getMergedData("students");

    setIsOpportunitiesLoading(false);
    setTimeLogs(timeLogs);
    setOpportunities(fetchedOpportunities);
    setStudents(fetchedStudents);
  }, []);

  // Save time logs to localStorage whenever they change
  useEffect(() => {
    if (timeLogs.length > 0) {
      updateStoredData("timeLogs", timeLogs);
    }
  }, [timeLogs]);

  // Handle adding a new time log
  const handleAddTimeLog = (newTimeLog) => {
    const timeLogWithId = {
      ...newTimeLog,
      id: Date.now() + Math.random(),
      status: "pending",
    };

    const updatedTimeLogs = [...timeLogs, timeLogWithId].sort((a, b) => {
      const statusOrder = { pending: 0, approved: 1, rejected: 2 };
      return statusOrder[a.status] - statusOrder[b.status];
    });

    setTimeLogs(updatedTimeLogs);
    updateStoredData("timeLogs", updatedTimeLogs);

    // Update opportunities if needed
    if (newTimeLog.opportunityId) {
      const updatedOpportunities = opportunities.map((opportunity) => {
        if (opportunity.id === newTimeLog.opportunityId) {
          const currentStudents = opportunity.students || [];
          if (!currentStudents.includes(newTimeLog.studentId)) {
            return {
              ...opportunity,
              students: [...currentStudents, newTimeLog.studentId],
            };
          }
        }
        return opportunity;
      });

      setOpportunities(updatedOpportunities);
      updateStoredData("opportunities", updatedOpportunities);
    }

    toast.success("Time log added successfully!");
    setIsAddModalOpen(false);
  };

  // Handle editing a time log
  const handleEditTimeLog = (updatedTimeLog) => {
    const updatedTimeLogs = timeLogs
      .map((log) =>
        log.id === updatedTimeLog.id ? { ...updatedTimeLog } : log
      )
      .sort((a, b) => {
        const statusOrder = { pending: 0, approved: 1, rejected: 2 };
        return statusOrder[a.status] - statusOrder[b.status];
      });

    setTimeLogs(updatedTimeLogs);
    updateStoredData("timeLogs", updatedTimeLogs);
    setIsEditModalOpen(false);
    toast.success("Time log updated successfully!");
  };

  // Handle deleting a time log
  const handleDeleteTimeLog = (id) => {
    if (window.confirm("Are you sure you want to delete this time log?")) {
      const updatedTimeLogs = timeLogs.filter((log) => log.id !== id);
      setTimeLogs(updatedTimeLogs);
      toast.success("Time log deleted successfully!");
    }
  };

  // Handle bulk upload of time logs
  const handleBulkUpload = (bulkTimeLogs) => {
    setTimeLogs([...timeLogs, ...bulkTimeLogs]);
  };

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-screen">
        <div className="text-gray-600">Loading time logs...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-3xl font-bold text-gray-800">Time Logs</h2>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md flex items-center justify-center shadow-md transition duration-300 w-full sm:w-auto"
          >
            <Plus size={20} className="mr-2" />
            Add Time Log
          </button>
          <button
            onClick={() => setIsBulkUploadOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-md flex items-center justify-center shadow-md transition duration-300 w-full sm:w-auto"
          >
            <Upload size={20} className="mr-2" />
            Bulk Upload
          </button>
        </div>
      </div>

      {/* Time Logs Table */}
      <TimeLogsTable
        timeLogs={timeLogs}
        students={students}
        opportunities={opportunities}
        onEdit={(log) => {
          setCurrentTimeLog(log);
          setIsEditModalOpen(true);
        }}
        onDelete={handleDeleteTimeLog}
      />

      {/* Add Time Log Modal */}
      {isOpportunitiesLoading ? (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded shadow-md">
            Loading Opportunities...
          </div>
        </div>
      ) : (
        <AddTimeLogModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSubmit={handleAddTimeLog}
          opportunities={opportunities}
          students={students}
        />
      )}

      {/* Edit Time Log Modal */}
      {currentTimeLog && (
        <EditTimeLogModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleEditTimeLog}
          timeLog={currentTimeLog}
          opportunities={opportunities}
          students={students}
        />
      )}

      {/* Bulk Upload Time Logs Modal */}
      <BulkUploadTimeLogs
        isOpen={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        onBulkUpload={handleBulkUpload}
        opportunities={opportunities}
        students={students}
      />
    </div>
  );
};

export default TimeLogs;
